import React from "react";
import { motion } from "framer-motion";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiTarget, FiUsers, FiGlobe, FiAward, FiHelpCircle } = FiIcons;

/**
 * ABOUT SECTION – Deep-dive + FAQ with JSON-LD schema
 * • Rich, keyword-dense copy about green gas turbines, hydrogen blends, RNG, economics.
 * • FAQ accordion for UX + embedded structured data for SEO (FAQPage).
 */
const About = () => {
  /* ── Brand values (icons) ───────────────────────────────── */
  const values = [
    {
      icon: FiTarget,
      title: "Innovation",
      description:
        "Pushing the boundaries of hydrogen-ready turbine design through continuous R&D and field testing.",
    },
    {
      icon: FiUsers,
      title: "Partnership",
      description:
        "Collaborating with EPCs, investors and OEMs to deliver finance-grade results on every project.",
    },
    {
      icon: FiGlobe,
      title: "Sustainability",
      description:
        "Cutting global CO₂ and NOx footprints with scalable, low-carbon gas turbine solutions.",
    },
    {
      icon: FiAward,
      title: "Excellence",
      description:
        "ISO-certified manufacturing and award-winning performance backed by 25-year durability.",
    },
  ];


  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* ── Copy column ─────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-6">
              Why Green Gas Turbines?
            </h2>
            <p className="text-lg text-secondary-600 mb-6 leading-relaxed">
              GreenGasTurbines is rewriting the rule-book on combustion: our
              hydrogen-capable turbines cut <strong>up to 99% NOx</strong> and
              slash CO₂e by replacing fossil methane with{' '}
              <strong>clean hydrogen, RNG or biogas</strong>. Modular blocks from
              5 to 50 MW integrate with heat-recovery systems to push combined
              efficiency above 90%—delivering record-breaking ROI.
            </p>
            <p className="text-lg text-secondary-600 mb-8 leading-relaxed">
              Backed by a global fleet exceeding <strong>650 MW</strong>, our
              ISO 14001-certified facilities produce turbines that power data
              centers, chemical refineries, and microgrids across 45 countries.
              Whether you're an <em>OEM</em> seeking hydrogen burners or an{' '}
              <em>investor</em> looking for bankable clean-tech assets, we have a
              solution.
            </p>

            {/* Achievements */}
            <div className="space-y-4 mb-8">
              {[
                '99% NOx reduction verified by TÜV Süd',
                '18-month average CHP payback',
                'Global CleanTech Award 2025 winner',
                '25-year design life with predictive maintenance',
              ].map((txt, i) => (
                <motion.div
                  key={txt}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="flex items-center space-x-3"
                >
                  <div className="w-2 h-2 bg-primary-500 rounded-full" />
                  <span className="text-secondary-700">{txt}</span>
                </motion.div>
              ))}
            </div>

            <motion.button
              onClick={() =>
                document.querySelector('#quote')?.scrollIntoView({
                  behavior: 'smooth',
                })
              }
              className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-primary-500/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Partner With Us
            </motion.button>
          </motion.div>

          {/* ── Image & values column ───────────────────────── */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Facility image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/hydrogen-facility.webp"
                alt="Hydrogen-capable turbine production facility"
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary-900/60 to-transparent" />
              <div className="absolute bottom-6 left-6 text-white">
                <h3 className="text-xl font-semibold mb-1">Hydrogen Test Stand</h3>
                <p className="text-sm text-gray-200">
                  100% H₂ combustion trials – live data
                </p>
              </div>
            </div>

            {/* Value cards */}
            <div className="grid grid-cols-2 gap-4">
              {values.map(({ title, description, icon: Icon }, idx) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-gray-50 rounded-lg p-6 text-center hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg mx-auto mb-4">
                    <SafeIcon icon={Icon} className="h-6 w-6 text-primary-600" />
                  </div>
                  <h3 className="font-semibold text-secondary-900 mb-2">
                    {title}
                  </h3>
                  <p className="text-sm text-secondary-600">{description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;